import React, {
  useState,
  useEffect,
} from 'react';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import PropTypes from 'prop-types';
import {
  makeStyles,
  TextField,
  ListItemText,
  Chip,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { getEventTemplates } from '@pingum/app-state-management/eventTemplate/actions';

import StatusBadge from '../../widgets/StatusBadge';
import TagList from '../../widgets/TagList';
import styles from './styles';

const useStyles = makeStyles(styles);

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay],
  );

  return debouncedValue;
};

export const EventTemplateAutocompleteComponent = (props) => {
  const {
    disabled,
    multiple,
    error,
    helperText,
    value,
    onChange,
  } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  // redux state
  const { content: eventTemplateSuggestions } = useSelector((state) => state.eventTemplate.eventTemplatesPageable);
  // local state
  const [inputValue, setInputValue] = useState('');
  const [pagination] = useState({
    page: 0,
    size: 10,
    search: '',
    sort: ['name,asc'],
  });

  const searchEventTemplates = () => {
    const paginationCopy = pagination;
    paginationCopy.search = inputValue;
    dispatch(getEventTemplates(false, paginationCopy));
  };

  const debouncedSearchTerm = useDebounce(inputValue, 500);

  useEffect(
    () => {
      if (debouncedSearchTerm) {
        searchEventTemplates();
      }
    },
    [debouncedSearchTerm], // Only call effect if debounced search term changes
  );

  const handleInputChange = (e) => {
    const changedValue = e.target.value;
    setInputValue(changedValue);
  };

  const handleFilterOptions = (options) => {
    if (!multiple) {
      return options;
    }
    const returnOptions = [];
    for (let i = 0; i < options.length; i += 1) {
      if (value.map((eventTemplate) => eventTemplate.id).indexOf(options[i].id) < 0) {
        returnOptions.push(options[i]);
      }
    }
    return returnOptions;
  };

  const renderSuggestion = (
    suggestion,
  ) => (
    <React.Fragment>
      <StatusBadge
        icon={suggestion.defaultStartStatus.icon}
        iconColor={suggestion.defaultStartStatus.iconColor}
      />
      <ListItemText
        className={classes.suggestionListItemText}
        primaryTypographyProps={{ noWrap: true }}
        primary={suggestion.name}
        secondary={<TagList tags={suggestion.tags} />}
        secondaryTypographyProps={{ component: 'div' }}
      />
    </React.Fragment>
  );

  return (
    <Autocomplete
      disabled={disabled}
      multiple={multiple}
      id="select-templates-outlined"
      options={eventTemplateSuggestions}
      filterSelectedOptions
      getOptionLabel={(option) => option.name}
      filterOptions={handleFilterOptions}
      value={value}
      onChange={onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          helperText={helperText}
          margin="normal"
          variant="outlined"
          label="Form Workflows"
          placeholder="Search Workflows"
          fullWidth
          onClick={searchEventTemplates}
          onChange={handleInputChange}
          value={inputValue}
        />
      )}
      renderOption={renderSuggestion}
      renderTags={(tagValue, getTagProps) => tagValue.map((et, index) => (
        <Chip label={et.name} {...getTagProps({ index })} />
      ))}
      disableCloseOnSelect
    />
  );
};

EventTemplateAutocompleteComponent.propTypes = {
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
};
EventTemplateAutocompleteComponent.defaultProps = {
  multiple: false,
  disabled: false,
  error: false,
  helperText: '',
  value: null,
  onChange: () => {},
};

export default (EventTemplateAutocompleteComponent);
